<template>
  <LayoutDefault>
    <ContentTitle ref="top" title="소식톡톡 반복/맞춤 소식"></ContentTitle>

    <form @submit.prevent="loadData(false)">
      <div class="section_search">
        <div class="tray">
          <div class="row">
            <span class="label">조회기간</span>
            <div class="cell">
              <PeriodPicker
                :startDate="$utils.calcDate(null, '-1m')"
                :endDate="$utils.calcDate(null, '5m')"
                :minDate="new Date('2019')"
                :maxDate="$utils.calcDate(null, '3y')"
                maxPeriod="6m"
                @maxPeriodError="onMaxPeriodError($refs.PeriodPicker)"
                ref="PeriodPicker"
              />

              <select v-model="formData.searchNewsType" :disabled="!permissions.search.range.modifiable">
                <option value="self">작성한 소식</option>
                <option value="all">전체 소식</option>
              </select>

              <select v-model="formData.newsType">
                <option value>소식종류</option>
                <option value="REPEAT">반복소식</option>
                <option value="CUSTOM">맞춤소식</option>
              </select>
            </div>
          </div>
          <div class="row">
            <span class="label">받는사람</span>
            <div class="cell">
              <div class="tray">
                <div class="cell">
                  <select v-model="formData.rcpGroupSendYn">
                    <option value>전체</option>
                    <option value="Y">그룹전송</option>
                    <option value="N">대상자별전송</option>
                  </select>
                </div>
                <span class="label">지역</span>
                <div class="cell">
                  <GroupSelector permissionCode="search" ref="groupSelector" />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <span class="label">검색</span>
            <div class="cell">
              <select v-model="formData.searchType" title="검색 필터">
                <option value="rcpName">받는사람</option>
                <option value="userName">작성자</option>
                <option value="tid">TID</option>
                <option value="content">내용</option>
              </select>
              <input
                type="text"
                placeholder="ex) 받는사람, 작성자, 내용 검색"
                class="inp_text blank"
                style="width: 657px"
                v-model.trim="formData.searchValue"
                title="검색어 입력"
                ref="searchInput"
              />
              <button type="submit" class="btn_search">서치</button>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- 테이블 -->
    <PagingTable ref="table" :sourceList="tableItems" :checkedList.sync="checkedList">
      <template #header>
        <caption>
          <span class="blind">소식관리 목록</span>
        </caption>
        <colgroup>
          <col width="5.5%" />
          <col width="5%" />
          <col width="6.5%" />
          <col width="15%" />
          <col />
          <col width="10%" />
          <col width="7%" />
          <col width="5%" />
          <col width="10%" />
          <col width="5%" />
          <col width="9.5%" />
        </colgroup>

        <thead class="table_list_head">
          <tr>
            <th>
              <CheckAllButton :targetTable="$refs.table" />
            </th>
            <th data-sort="id">번호</th>
            <th class="center" data-sort="newsType,customNewsType">소식종류</th>
            <th class="center" data-sort="rcpDesc">받는사람</th>
            <th class="center" data-sort="boardTitle">소식 내용</th>
            <th class="center" data-sort="userName">작성자</th>
            <th class="center" data-sort="validFrom">유효기간</th>
            <th class="center" data-sort="reserveTime">예약시간</th>
            <th class="center" data-sort="repeatStrategy.period">전송주기</th>
            <th class="center" data-sort="sendCount">전송이력</th>
            <th>수정/삭제</th>
          </tr>
        </thead>
      </template>
      <template #row="{rowData}">
        <tr>
          <td>
            <input type="checkbox" v-model="rowData._checked" />
          </td>
          <td>{{ rowData.id }}</td>
          <RepeatTypeTextColumn :newsType="rowData.newsType" :customNewsType="rowData.customNewsType" :short="true" />
          <RcpDescColumn
            :useLink="!!rowData.onlyOneRcpId"
            :linkUrl="`/news/recipient/${rowData.onlyOneRcpId}/repeat`"
            :content="rowData.rcpDesc"
            @popupClick="openRepeatRecipientList({newsId: rowData.id})"
          />
          <EditedTextColumn :value="rowData.boardContent" :rowData="rowData" @click="openRepeatDetail({newsId: rowData.id})" />
          <td>{{ rowData.userName }}</td>
          <PeriodColumn :start="rowData.validFrom" :end="rowData.validTo" />
          <RepeatReserveTimeColumn :rowData="rowData" />
          <RepeatStrategyColumn :value="rowData.repeatStrategy" />
          <td>
            <button v-if="rowData.sendCount" type="button" class="btn_function blue" @click="openRepeatSendLogsByRowData(rowData)">
              보기
            </button>
            <span v-else>없음</span>
          </td>
          <td>
            <template v-if="rowData.modifiable">
              <button type="button" class="btn_function blue" @click="onClickEditBtn(rowData)">수정</button>
              <button type="button" class="btn_function" @click="deleteNews(rowData.id, rowData.newsType)">삭제</button>
            </template>
            <span v-else>-</span>
          </td>
        </tr>
      </template>
    </PagingTable>

    <div class="section_button">
      <ExcelDownloadButton :filename="$utils.excelName('반복맞춤소식')">
        <ExcelTable :sourceList="tableItems" />
      </ExcelDownloadButton>
      <HelpButton contentId="HELP_01" />
      <button type="button" class="btn_register" @click="deleteNewsBulk(_.map(checkedList, (item) => item.id))" :disabled="!isRegister">
        삭제
      </button>
      <button type="button" class="btn_register" @click="openRepeatRegister({onSubmit: loadDataAndKeepOptions})" :disabled="!isRegister">등록</button>
    </div>
  </LayoutDefault>
</template>

<script>
import newsMixin from '$views/News/_mixin';
import periodPickerMixin from '$mixins/periodPicker';
import ExcelTable from './excelTables/All.Repeat';

export default {
  mixins: [newsMixin, periodPickerMixin],
  components: {
    ExcelTable,
  },
  data() {
    const initialNewsType = _.get(this.$store.state, 'permissions.search.range.type');
    return {
      tableItems: null,
      checkedList: [],
      currentRequestOptions: {},
      formData: {
        searchStartDate: '',
        searchEndDate: '',
        searchNewsType: initialNewsType ? initialNewsType.toLowerCase() : 'all',
        orgCode: '',
        rcpState: '',
        addrCity: '',
        rcpGroup: '',
        pushStatus: '',
        searchType: 'content',
        searchValue: '',
        newsType: '',
        rcpGroupSendYn: '',
      },
    };
  },
  computed: {},
  methods: {
    onClickEditBtn(rowData) {
      this.openRepeatUpdater({
        newsId: rowData.id,
        newsType: rowData.newsType,
        customNewsType: rowData.customNewsType,
        onSubmit: () => this.loadDataAndKeepOptions(true),
      });
    },
    requestOptions() {
      const period = this.$refs.PeriodPicker.getData();
      const group = this.$refs.groupSelector.getData();

      return {
        params: _.merge(this.formData, {
          searchStartDate: this.$utils.dateText(period.startDate, ''),
          searchEndDate: this.$utils.dateText(period.endDate, ''),
          orgCode: group.orgId,
          rcpState: group.stateId,
          addrCity: group.cityId,
          rcpGroup: group.groupId,
        }),
      };
    },
    keepRequestOptions() {
      _.merge(this.formData, this.currentRequestOptions);
      this.$refs.PeriodPicker.onSetDate(new Date(this.currentRequestOptions.searchStartDate),
          new Date(this.currentRequestOptions.searchEndDate));
      this.$refs.groupSelector.applyData(this.currentRequestOptions.orgCode, this.currentRequestOptions.rcpState,
          this.currentRequestOptions.addrCity, this.currentRequestOptions.rcpGroup);
    },
    loadData(keepTableState) {
      if (this.$utils.hasSpecialChar(this.formData.searchValue)) {
        alert('특수문자를 제외하고 검색해 주세요.');
        this.$refs.searchInput.focus();
        return;
      }
      const options = this.requestOptions();
      this.$axios.get('/admin/v2/news/repeat/lists', options).then(({data}) => {
        this.tableItems = data.data;
        _.merge(this.currentRequestOptions, options.params);
        if (!keepTableState) this.$refs.table.resetState();
      });
    },
    loadDataAndKeepOptions(keepTableState) {
      const options = {
        params: this.currentRequestOptions,
      }
      this.$axios.get('/admin/v2/news/repeat/lists', options).then(({data}) => {
        this.keepRequestOptions();
        this.tableItems = data.data;
        if (!keepTableState) this.$refs.table.resetState();
      });
    }
  },
  mounted() {
    this.loadData();
  },
};
</script>
